import type { PlanDefinition, PlanDefinitionAction } from '@medplum/fhirtypes';

import {
  type ActivityCompiler,
  type CompiledPlan,
  type DecodeContext,
  buildPlanDefinitionBase,
  createActivityCompiler,
  decodeActivityActions,
  decodeSharedFields,
  isActivityAction,
  sectionCodeOf,
  sectionConcept,
  toFhirGoal,
} from './shared';
import type { EpisodicPlanSpec, PlanEventSpec } from './spec';

function eventToAction(
  event: PlanEventSpec,
  index: number,
  activities: ActivityCompiler,
): PlanDefinitionAction {
  return {
    id: `event-${index + 1}`,
    title: event.name,
    description: event.description,
    code: [sectionConcept('event', 'Event')],
    trigger: [{ type: 'named-event', name: event.name }],
    // The response is what care is delivered when the event fires — a nested
    // action under the triggered one, with any assigned activities as tagged
    // siblings.
    action: [
      { title: 'Response', description: event.response },
      ...(activities.compile(event.activities, `event-${index + 1}`) ?? []),
    ],
  };
}

function actionToEvent(
  action: PlanDefinitionAction,
  ctx?: DecodeContext,
): PlanEventSpec | undefined {
  const name = action.title ?? action.trigger?.[0]?.name;
  const children = action.action ?? [];
  const response = children.find((a) => !isActivityAction(a))?.description;
  if (!name || !response) {
    return undefined;
  }
  return {
    name,
    description: action.description,
    response,
    activities: decodeActivityActions(children, ctx),
  };
}

export function episodicSpecToResources(spec: EpisodicPlanSpec): CompiledPlan {
  const activities = createActivityCompiler(spec.title);
  return {
    planDefinition: {
      ...buildPlanDefinitionBase(
        spec,
        'episodic',
        (spec.goals ?? []).map((g) => toFhirGoal(g)),
      ),
      action: [
        {
          id: 'baseline',
          title: 'Baseline',
          description: spec.baseline,
          code: [sectionConcept('baseline', 'Baseline')],
          // The standing home program: activities assigned as baseline care.
          action: activities.compile(spec.baselineActivities, 'baseline'),
        },
        ...spec.events.map((event, i) => eventToAction(event, i, activities)),
      ],
    },
    activityDefinitions: activities.definitions(),
  };
}

export function episodicFhirToSpec(
  pd: PlanDefinition,
  ctx?: DecodeContext,
): EpisodicPlanSpec | null {
  const actions = pd.action ?? [];
  // Tolerant slotting: prefer section codes; fall back to trigger presence
  // (events) vs not (baseline).
  const baseline =
    actions.find((a) => sectionCodeOf(a.code) === 'baseline') ??
    actions.find(
      (a) => !a.trigger?.length && sectionCodeOf(a.code) === undefined,
    );
  const events = actions
    .filter((a) => a !== baseline)
    .filter(
      (a) => sectionCodeOf(a.code) === 'event' || (a.trigger?.length ?? 0) > 0,
    )
    .map((a) => actionToEvent(a, ctx))
    .filter((e): e is PlanEventSpec => e !== undefined);
  if (!baseline?.description || events.length === 0) {
    return null;
  }
  return {
    shape: 'episodic',
    ...decodeSharedFields(pd),
    baseline: baseline.description,
    baselineActivities: decodeActivityActions(baseline.action, ctx),
    events,
  };
}
